import { Box } from "@mui/material";
import { brainTokens } from "../design/brainTheme";

const c = brainTokens.color;

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return (parts[0]?.slice(0, 2) || "?").toUpperCase();
}

function tintFor(seed: string): [string, string] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  const hues = [168, 200, 24, 280, 140, 320];
  const hue = hues[h % hues.length]!;
  return [`hsl(${hue} 42% 92%)`, `hsl(${hue} 35% 32%)`];
}

/**
 * Photo-or-initials person avatar. Falls back to a tinted monogram when
 * avatarUrl is missing — shared across Library, Who-are-we, person pages.
 */
export function PersonAvatar({
  name,
  avatarUrl,
  size = 40,
  presence,
}: {
  name: string;
  avatarUrl?: string | null;
  size?: number;
  /** Optional presence dot: active | held-unpushed | idle */
  presence?: "active" | "held-unpushed" | "idle" | null;
}) {
  const [bg, fg] = tintFor(name || "?");
  const radius = Math.round(size * 0.28);
  const dotColor =
    presence === "active" ? c.successText : presence === "held-unpushed" ? c.warnText : presence === "idle" ? c.faint : null;

  return (
    <Box sx={{ position: "relative", width: size, height: size, flex: "none" }}>
      {avatarUrl ? (
        <Box
          component="img"
          src={avatarUrl}
          alt={name}
          sx={{
            width: size,
            height: size,
            borderRadius: `${radius}px`,
            objectFit: "cover",
            display: "block",
            boxShadow: `inset 0 0 0 1px ${c.border}`,
          }}
        />
      ) : (
        <Box
          sx={{
            width: size,
            height: size,
            borderRadius: `${radius}px`,
            background: bg,
            color: fg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: Math.round(size * 0.36),
            fontWeight: 700,
            fontFamily: brainTokens.font.sans,
          }}
        >
          {initials(name)}
        </Box>
      )}
      {dotColor && (
        <Box
          sx={{
            position: "absolute",
            right: -1,
            bottom: -1,
            width: Math.max(8, Math.round(size * 0.22)),
            height: Math.max(8, Math.round(size * 0.22)),
            borderRadius: "50%",
            background: dotColor,
            boxShadow: `0 0 0 2px ${c.pageBg}`,
          }}
        />
      )}
    </Box>
  );
}
